import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { TipoGanado } from './tipo-ganado';

export interface TiposGanadoState {
  list: PaginatedList<TipoGanado>;
  current: TipoGanado;
  actives: TipoGanado[];

  nuevo: () => void;
  cargarPaginados: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivos: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarTipoGanado) => Promise<boolean>;
  actualizar: (id: number, request: ActualizarTipoGanado) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarTipoGanado {
  descripcion: string;
}

export interface ActualizarTipoGanado {
  descripcion: string;
}

const { showSuccessToast } = useUIStore.getState();

export const useTiposGanadoStore = create<TiposGanadoState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as TipoGanado,
  actives: [],

  nuevo: () =>
    set({
      current: {} as TipoGanado
    }),
  cargarPaginados: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<TipoGanado>>(
      `/tipos-ganado/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivos: async () => {
    const data = await makeGet<Array<TipoGanado>>('/tipos-ganado/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<TipoGanado>(`/tipos-ganado/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarTipoGanado) => {
    const data = await makePost<RegistrarTipoGanado, TipoGanado>('/tipos-ganado/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as TipoGanado
    });

    return true;
  },
  actualizar: async (id: number, request: ActualizarTipoGanado) => {
    const data = await makePatch<ActualizarTipoGanado, TipoGanado>(`/tipos-ganado/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/tipos-ganado/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/tipos-ganado/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  }
}));
